import { ErrorCode, Country } from '@afterpaytouch/portal-api/types'
import { unwrapResult } from '@reduxjs/toolkit'
import { isPortalApiError } from '@afterpaytouch/portal-api/utils'
import {
  accountLookup,
  authCheckChallenge,
  authCheckCode,
  authResetCode,
  ChallengeState,
  check2faDob,
  CheckoutAppSelector,
  PasswordResetState,
  useAppDispatch,
  useAppSelector,
  useConsumerSelector,
  useSMSEnabled,
} from '../..'
import { Route, usePostLoginRoute } from '../../../config/router'
import { markUsFebConsentInteractiveLogin } from '../../../flow/one-offer-page/consentLoginSignal'
import {
  AuthCheckCodeStatus,
  PasswordResetTwoFactorChallenge,
  TwoFactorChallengeType,
  VerificationError,
  VerificationErrorCode,
  VerificationFlow,
  VerificationFlowContext,
  VerificationMethod,
  VerifyChallengeFlow,
  VerificationFlowRoute,
  CodeVerificationDirective,
} from '../../../model'
import { useMobileLogin } from '../user/hooks'
import { useIsApplePaylater } from '../../applePaylater/hooks'

export const resetPasswordSelector: CheckoutAppSelector<PasswordResetState> = (state) => state.auth.resetPassword
export const resetPasswordChallengeSelector: CheckoutAppSelector<ChallengeState | undefined> = (state) => state.auth.resetPassword.challenge
export const twoFactorChallengeTypeSelector: CheckoutAppSelector<PasswordResetTwoFactorChallenge[]> = (state) =>
  state.auth.resetPassword.challenge?.challenges ?? []
export const initialMethodSelector: CheckoutAppSelector<VerificationMethod | undefined> = (state) => state.auth.resetPassword.initialMethod
export const autoSendSmsSelector: CheckoutAppSelector<boolean> = (state) => state.auth.resetPassword.autoSendSms

export const useResetPassword = (): PasswordResetState => useAppSelector(resetPasswordSelector)
export const useChallengeSelector = (): ChallengeState | undefined => useAppSelector(resetPasswordChallengeSelector)
export const useTwoFactorChallengeSelector = (): PasswordResetTwoFactorChallenge[] => useAppSelector(twoFactorChallengeTypeSelector)
export const useInitialMethodSelector = (): VerificationMethod | undefined => useAppSelector(initialMethodSelector)
export const useAutoSendSmsSelector = (): boolean => useAppSelector(autoSendSmsSelector)

const errors: Partial<Record<ErrorCode, VerificationErrorCode>> = {
  [ErrorCode.SmsMessagingNotEnabled]: VerificationErrorCode.MethodNotEnabled,
  [ErrorCode.PasswordResetCodeGenerationNotAllowed]: VerificationErrorCode.MaxAttemptsReached,
  [ErrorCode.PasswordResetDeclined]: VerificationErrorCode.PasswordResetDeclined,
  [ErrorCode.NotFound]: VerificationErrorCode.CodeInvalid,
  [ErrorCode.PasswordResetCodeInvalid]: VerificationErrorCode.CodeInvalid,
  [ErrorCode.PasswordResetCodeMaxAttempts]: VerificationErrorCode.MaxAttemptsReached,
  [ErrorCode.PasswordResetTwoFactorChallengesInvalid]: VerificationErrorCode.PasswordResetTwoFactorChallengesInvalid,
  [ErrorCode.PasswordResetTwoFactorChallengesMaxAttempts]: VerificationErrorCode.PasswordResetTwoFactorChallengesMaxAttempts,
  [ErrorCode.InvalidDobVerification]: VerificationErrorCode.InvalidDob,
  [ErrorCode.MaxDobVerificationAttempts]: VerificationErrorCode.MaxAttemptsReached,
  [ErrorCode.PaywatchCheckFailed]: VerificationErrorCode.PaywatchCheckFailed,
  [ErrorCode.AccountDisabled]: VerificationErrorCode.AccountDisabled,
  [ErrorCode.AccountClosed]: VerificationErrorCode.AccountClosed,
  [ErrorCode.MaxAttemptsReached]: VerificationErrorCode.MaxAttemptsReached,
  [ErrorCode.MaxSmsCodeAttempts]: VerificationErrorCode.MaxSmsCodeAttempts,
  [ErrorCode.PasswordResetNotAllowed]: VerificationErrorCode.PasswordResetNotAllowed,
  [ErrorCode.MaxEmailCodeAttempts]: VerificationErrorCode.MaxAttemptsReached,
}

export const usePasswordResetFlow = (): VerificationFlow => {
  const { verificationMethods: enabled } = useResetPassword()
  const autoSendCode = useAutoSendSmsSelector()
  const dispatch = useAppDispatch()
  const { hasPassword, mobile } = useConsumerSelector()
  const mobileLogin = useMobileLogin()
  const smsEnabled = useSMSEnabled()
  const skipPasswordCreation = useIsApplePaylater()
  const postLoginRoute = usePostLoginRoute()

  const sendCode = async (method: VerificationMethod): Promise<CodeVerificationDirective | undefined> => {
    try {
      return unwrapResult(await dispatch(authResetCode({ sendBy: method })))
    } catch (e) {
      const errorCode = isPortalApiError(e) ? e.errorCode : 'ERROR'
      throw new VerificationError(errors[errorCode])
    }
  }
  const checkCode = async (code: string): Promise<VerificationFlowRoute> => {
    try {
      const result = unwrapResult(await dispatch(authCheckCode({ code })))
      if (result?.status === AuthCheckCodeStatus.TWO_FACTOR_REQUIRED && !mobileLogin?.hasSeenChallenge) {
        return { route: Route.VERIFY_CHALLENGE }
      } else if (skipPasswordCreation) {
        try {
          const account = unwrapResult(await dispatch(accountLookup()))
          if (postLoginRoute === Route.LOGGED_IN) {
            markUsFebConsentInteractiveLogin()
          }
          if (!account.profileConfirmed) {
            return { route: Route.SIGN_UP }
          }
          return { route: postLoginRoute, locale: account.preferredLocale }
        } catch {
          return { route: Route.TERMINAL_ERROR }
        }
      }
      return { route: Route.CREATE_PASSWORD }
    } catch (e) {
      const errorCode = isPortalApiError(e) ? e.errorCode : 'ERROR'
      throw new VerificationError(errors[errorCode])
    }
  }
  const checkDob = async (dateOfBirth: Date, lastName: string): Promise<void> => {
    try {
      unwrapResult(
        await dispatch(
          check2faDob({
            mobile: mobile ?? '',
            dateOfBirth,
            lastName,
          })
        )
      )
    } catch (e) {
      const errorCode = isPortalApiError(e) ? e.errorCode : 'ERROR'
      throw new VerificationError(errors[errorCode])
    }
  }
  const previousRoute = hasPassword ? Route.ENTER_PASSWORD : Route.ENTER_EMAIL

  return {
    enabled,
    sendCode,
    checkCode,
    autoSendCode,
    initialMethod: smsEnabled ? VerificationMethod.SMS : VerificationMethod.EMAIL,
    context: hasPassword ? VerificationFlowContext.Reset : VerificationFlowContext.FirstPassword,
    allowLogout: !hasPassword,
    revealMobile: false,
    previousRoute,
    prefetchRoutes: [previousRoute, Route.VERIFY_CHALLENGE, ...(skipPasswordCreation ? [postLoginRoute] : [Route.CREATE_PASSWORD])],
    checkDob,
  }
}

export const useVerifyChallengeFlow = (): VerifyChallengeFlow => {
  const dispatch = useAppDispatch()
  const checkChallenge = async (
    email: string,
    code: number,
    twoFactorChallenges: Partial<Record<TwoFactorChallengeType, string>>,
    countryCode: Country
  ): Promise<void> => {
    try {
      return unwrapResult(await dispatch(authCheckChallenge({ email, code, twoFactorChallenges, countryCode })))
    } catch (e) {
      const errorCode = isPortalApiError(e) ? e.errorCode : 'ERROR'
      throw new VerificationError(errors[errorCode])
    }
  }
  return { checkChallenge }
}
